import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import styled from 'styled-components';
import { Small } from 'foundations-components/transitional/components/Small';
import { usePrivacyBanner } from './usePrivacyBanner';
import { StyledUILink } from './TextInput.styles';
import { getLegalLink } from '../legal/getLegalLink';
import HubletLocation from './HubletLocation';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { LayoutType } from 'signup-components/types';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTermsOfServiceLink = styled.span.withConfig({
  displayName: "CreateAccountTermsOfService__StyledTermsOfServiceLink"
})(({
  theme
}) => {
  return {
    color: theme.color['text-core-default'],
    typography: theme.typography['detail-200'],
    textDecoration: 'underline',
    WebkitFontSmoothing: 'auto',
    MozOsxFontSmoothing: 'auto'
  };
});
const TermsOfServiceLinkWrapper = props => {
  const {
    layoutType
  } = useLayoutTemplate();
  if (layoutType === LayoutType.SidePanel) {
    return /*#__PURE__*/_jsx(Link, Object.assign({}, props, {
      use: "unstyled",
      children: /*#__PURE__*/_jsx(StyledTermsOfServiceLink, {
        children: props.children
      })
    }));
  }
  return /*#__PURE__*/_jsx(StyledUILink, Object.assign({}, props, {
    children: props.children
  }));
};
const StyledPrivacyBanner = styled.div.withConfig({
  displayName: "CreateAccountTermsOfService__StyledPrivacyBanner"
})(["margin-top:", ";text-align:center;display:flex;flex-direction:column;gap:", ";", " ", ""], ({
  theme
}) => theme.space['500'], ({
  theme
}) => theme.space['300'], props => props.$shouldShowToSOptionalStep && !props.$hideBorder && ` {
    border-top: 1px solid ${props.theme.color['border-core-default']};
    padding-top: ${props.theme.space['500']};
    margin-top: ${props.theme.space['500']};
  }`, props => props.$isOnLandingPage && `{
    margin-top: 0;
  }`);
const CreateAccountTermsOfService = ({
  hideBorder = false
}) => {
  const {
    createAccountTextI18nLabel,
    handleClickTermsOfService,
    shouldShowToSOptionalStep,
    handleClickDeveloperTermsOfService,
    handleConnectTermsOfUseClick,
    handleClickPrivacyPolicy,
    isOnLandingPage
  } = usePrivacyBanner();
  const {
    layoutType
  } = useLayoutTemplate();
  const {
    isAssignedToSpecificVariant: isAssignedToACQ173Variant
  } = useExperiment(ExperimentKeys.ACQ0173, TrackWhen.enrolled);
  const isAssignedToACQ173VariantA = isAssignedToACQ173Variant(ExperimentParameterGroup.Variant_A);
  const isAssignedToACQ173VariantC = isAssignedToACQ173Variant(ExperimentParameterGroup.Variant_C);
  const termsOfServiceOptions = {
    termsOfServiceLink: /*#__PURE__*/_jsx(TermsOfServiceLinkWrapper, {
      external: true,
      hideExternalLinkIcon: true,
      href: getLegalLink('terms-of-service'),
      onClick: handleClickTermsOfService,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "legal.termsOfServiceLink"
      })
    }, "gdprPolicy"),
    developerTermsOfServiceLink: /*#__PURE__*/_jsx(TermsOfServiceLinkWrapper, {
      external: true,
      hideExternalLinkIcon: true,
      href: 'https://legal.hubspot.com/developer-terms',
      onClick: handleClickDeveloperTermsOfService,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "legal.developerTermsOfServiceLink"
      })
    }, "gdprPolicyDevelopers"),
    developerCustomerTermsOfServiceLink: /*#__PURE__*/_jsx(TermsOfServiceLinkWrapper, {
      external: true,
      hideExternalLinkIcon: true,
      href: getLegalLink('terms-of-service'),
      onClick: handleClickTermsOfService,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "legal.developerCustomerTermsOfServiceLink"
      })
    }, "gdprPolicyCustomers"),
    connectTermsOfServiceLink: /*#__PURE__*/_jsx(TermsOfServiceLinkWrapper, {
      external: true,
      hideExternalLinkIcon: true,
      href: getLegalLink('website-terms-of-use'),
      onClick: handleConnectTermsOfUseClick,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "legal.connectTermsOfServiceLink"
      })
    }, "gdprPolicy"),
    hubspotPrivacyPolicyLink: /*#__PURE__*/_jsx(TermsOfServiceLinkWrapper, {
      external: true,
      hideExternalLinkIcon: true,
      href: getLegalLink('privacy-policy'),
      onClick: handleClickPrivacyPolicy,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "legal.hubspotPrivacyPolicyLink"
      })
    }, "gdprPolicy")
  };
  if (layoutType === LayoutType.SidePanel || isAssignedToACQ173VariantC || isAssignedToACQ173VariantA) {
    return /*#__PURE__*/_jsxs(Text, {
      color: "text-core-default",
      marginInlineEnd: "400",
      textAlign: "right",
      textWrap: "balance",
      typography: "detail-200",
      children: [/*#__PURE__*/_jsx(HubletLocation, {}), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: createAccountTextI18nLabel,
        options: termsOfServiceOptions
      })]
    });
  }
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledPrivacyBanner, {
      $hideBorder: hideBorder,
      $isOnLandingPage: isOnLandingPage,
      $shouldShowToSOptionalStep: shouldShowToSOptionalStep,
      children: [/*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: createAccountTextI18nLabel,
          options: termsOfServiceOptions
        })
      }), /*#__PURE__*/_jsx(HubletLocation, {})]
    })
  );
};
export default CreateAccountTermsOfService;